import { useStore } from "vuex";
import { computed } from "vue";
import {
  EDUCATION_LEVELS,
  UNDERGRADUATE_LEARNER_TYPE_IDS,
  HIGH_SCHOOL_LEARNER_TYPE_IDS,
  NOT_ENROLLED_LEARNER_TYPE_IDS,
  CONTINUING_EDUCATION_LEARNER_TYPE_IDS,
  PROFESSIONAL_LEARNER_TYPE_IDS,
} from "@/constants";

/**
 * Controls which Learner Types should appear inside dropdown based on program session
 * @param {providedEducationLevels} Ref<Array> - expects computed, An array of objects see https://classes.apiabroad.com/api/v2/docs#/Education%20Levels/get_list_api_v2_education_levels__get for content
 * @param {selectedLearnerTypeId} Ref<String> - id/uuid of the selected learner type (Optional)
 */
export const useLearnerTypesFromProgramSession = (providedEducationLevels, selectedLearnerTypeId) => {
  const store = useStore();
  const learnerTypes = computed(() => store.getters["learnerTypes/getLearnerTypes"]);
  const profileData = computed(() => store.getters["getProfileData"]);
  const learnerTypeProfile = computed(() => profileData.value?.learner_type_id ?? "");

  const noEducationLevelsFound = computed(() => providedEducationLevels.value?.length <= 0);

  const undergraduateLearnerTypes = computed(() =>
    learnerTypes.value.filter((type) => UNDERGRADUATE_LEARNER_TYPE_IDS.some((opt) => type.id.includes(opt)))
  );

  const highSchoolLearnerTypes = computed(() =>
    learnerTypes.value.filter((type) => HIGH_SCHOOL_LEARNER_TYPE_IDS.some((opt) => type.id.includes(opt)))
  );

  const notEnrolledLearnerTypes = computed(() =>
    learnerTypes.value.filter((type) => NOT_ENROLLED_LEARNER_TYPE_IDS.some((opt) => type.id.includes(opt)))
  );

  const continuingEducationLearnerTypes = computed(() =>
    learnerTypes.value.filter((type) => CONTINUING_EDUCATION_LEARNER_TYPE_IDS.some((opt) => type.id.includes(opt)))
  );

  const professionalLearnerTypes = computed(() =>
    learnerTypes.value.filter((type) => PROFESSIONAL_LEARNER_TYPE_IDS.some((opt) => type.id.includes(opt)))
  );

  const filteredLearnerTypes = computed(() => {
    if (noEducationLevelsFound.value) {
      return learnerTypes.value;
    }

    const matchingLearnerTypes = [];
    (providedEducationLevels.value ?? []).forEach((educationLevel) => {
      if (educationLevel.value === EDUCATION_LEVELS.undergraduate) {
        matchingLearnerTypes.push(...undergraduateLearnerTypes.value);
      }

      if (educationLevel.value === EDUCATION_LEVELS.high_school) {
        matchingLearnerTypes.push(...highSchoolLearnerTypes.value);
      }

      if (educationLevel.value === EDUCATION_LEVELS.not_enrolled) {
        matchingLearnerTypes.push(...notEnrolledLearnerTypes.value);
      }

      if (educationLevel.value === EDUCATION_LEVELS.continuing_education) {
        matchingLearnerTypes.push(...continuingEducationLearnerTypes.value);
      }

      if (educationLevel.value === EDUCATION_LEVELS.professional) {
        matchingLearnerTypes.push(...professionalLearnerTypes.value);
      }
    });
    return matchingLearnerTypes
      .filter((type, i) => {
        // filtering so it does not have duplicates
        return matchingLearnerTypes.indexOf(type) === i;
      })
      .sort((a, b) => a?.value.localeCompare(b?.value));
  });

  const currentLearnerTypeIsValid = computed(() => {
    return filteredLearnerTypes.value.some((type) => type?.id === selectedLearnerTypeId.value);
  });

  const invalidLearnerTypeFromProfile = computed(() => {
    return (
      learnerTypeProfile.value && !filteredLearnerTypes.value.some((type) => type?.id === learnerTypeProfile.value)
    );
  });

  return { filteredLearnerTypes, currentLearnerTypeIsValid, invalidLearnerTypeFromProfile };
};
